/**
 * AbilitySlots — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.abilitySlots,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';
import { changed, invalidate } from './dirty.js';
import { pixiHudLogger } from './logger.js';

/** Weapon / ability HUD squares share this empty-vs-filled contrast. */
const EMPTY_SLOT_FILL_ALPHA = 0.35;
const FILLED_SLOT_FILL_ALPHA = 0.7;

/** Cooldown overlay uses the later of ability/global cooldown and Nullify silence. */
export function slotUnavailableUntil(cooldownEnd, silenceEnd) {
  return Math.max(cooldownEnd || 0, silenceEnd || 0);
}

/**
 * Create ability slots
 */
export function create(hud) {
  const container = new PIXI.Container();
  hud.hudContainer.addChild(container);

  const slots = [];
  const slotConfigs = [
    { category: 'utility', key: 'Q', color: 0xaa00ff },
    { category: 'movement', key: 'L SHFT', color: 0x00ffff }
  ];

  slotConfigs.forEach((config, index) => {
    const slotContainer = new PIXI.Container();
    container.addChild(slotContainer);

    // Background
    const background = new PIXI.Graphics();
    slotContainer.addChild(background);

    // Border
    const border = new PIXI.Graphics();
    slotContainer.addChild(border);

    // Icon sprite
    const icon = new PIXI.Sprite();
    icon.anchor.set(0.5, 0.5);
    slotContainer.addChild(icon);

    // Cooldown overlay
    const cooldownOverlay = new PIXI.Graphics();
    slotContainer.addChild(cooldownOverlay);

    // Cooldown text
    const cooldownText = hud._createText('', 16, 0xffffff, true);
    cooldownText.anchor.set(0.5, 0.5);
    slotContainer.addChild(cooldownText);

    // Effect bar
    const effectBar = new PIXI.Graphics();
    slotContainer.addChild(effectBar);

    // Stacks text
    const stacksText = hud._createText('', 14, 0xffffff, true);
    stacksText.anchor.set(1, 0);
    slotContainer.addChild(stacksText);

    // Key label
    const keyLabel = hud._createText(config.key, 12, 0xffffff, true);
    keyLabel.anchor.set(0.5, 1);
    slotContainer.addChild(keyLabel);

    slots.push({
      container: slotContainer,
      background, border, icon,
      cooldownOverlay, cooldownText,
      effectBar, stacksText, keyLabel,
      config
    });
  });

  hud.elements.abilitySlots = { container, slots };

  // Above the Tab overlay (100), like the inventory slots, so an ability can be
  // seen and dragged out while the inventory is open.
  container.zIndex = 200;
  hud._enableAbilityDragToDrop(slots);

  // Inventory slots container (5 boxes under ability slots)
  const invContainer = new PIXI.Container();
  invContainer.zIndex = 200; // Above overlay (100) so slots are clickable
  hud.hudContainer.addChild(invContainer);
  hud.hudContainer.sortableChildren = true; // Enable z-index sorting

  const invSlots = [];
  for (let i = 0; i < 5; i++) {
    const s = new PIXI.Container();
    invContainer.addChild(s);

    const bg = new PIXI.Graphics();
    s.addChild(bg);

    const border = new PIXI.Graphics();
    s.addChild(border);

    // Active slot highlight border
    const activeBorder = new PIXI.Graphics();
    s.addChild(activeBorder);

    // Icon border for non-selected slots
    const iconBorder = new PIXI.Graphics();
    s.addChild(iconBorder);

    // Icon sprite for weapon
    const icon = new PIXI.Sprite();
    icon.anchor.set(0.5, 0.5);
    s.addChild(icon);

    // Number label under slot
    const numText = hud._createText(String(i + 1), 14, 0xffffff, true);
    numText.anchor.set(0.5, 0);
    s.addChild(numText);

    // Stack indicator (x2, x3, etc.)
    const stackText = hud._createText('', 14, 0xffffff, true);
    stackText.anchor.set(1, 0);
    stackText.visible = false;
    s.addChild(stackText);

    // Ammo readout on the weapon tile: mag is the hero number on the
    // bottom-left; reserve is smaller and quieter on the bottom-right.
    const magText = hud._createText('', 14, 0x000000, true);
    magText.anchor.set(0, 1);
    magText.style.stroke = { color: 0xffffff, width: 4 };
    magText.visible = false;
    s.addChild(magText);

    const reserveText = hud._createText('', 11, 0x555555, true);
    reserveText.anchor.set(1, 1);
    reserveText.style.stroke = { color: 0xffffff, width: 4 };
    reserveText.visible = false;
    s.addChild(reserveText);

    invSlots.push({ container: s, background: bg, border, activeBorder, iconBorder, icon, numText, stackText, magText, reserveText });
  }

  hud.elements.inventorySlots = { container: invContainer, slots: invSlots };
  invContainer.visible = true; // Ensure container is visible
  pixiHudLogger.debug('[PixiHUD] Created inventory slots:', invSlots.length, 'slots, container visible:', invContainer.visible);
  
  // Enable drag-and-drop for inventory slot reordering
  hud._enableInventoryDragAndDrop(invSlots);
  
  // Position both ability and inventory slots now that both exist
  hud._positionAbilitySlots();
}

/**
 * Position ability slots
 */
export function position(hud) {
  const as = hud.elements.abilitySlots;
  if (!as) return;

  const slotSize = 90; // 20% larger than previous 75px
  const slotPadding = 10;
  const hudX = hud.screenWidth - 220; // Adjusted for larger slots
  const hudY = hud.screenHeight - 238; // 8px above the previous edge-flush ammo row

  as.slots.forEach((slot, index) => {
    const slotX = hudX + index * (slotSize + slotPadding);
    const slotY = hudY;

    slot.container.x = slotX;
    slot.container.y = slotY;

    // Background (filled alpha applied in update when an ability is equipped)
    slot.background.clear();
    slot.background.rect(0, 0, slotSize, slotSize);
    slot.background.fill({ color: 0x000000, alpha: EMPTY_SLOT_FILL_ALPHA });
    invalidate(slot);

    // Border (updated during render based on equipped state)
    slot.border.clear();
    slot.border.setStrokeStyle({ width: 2, color: 0x555555 });
    slot.border.rect(0, 0, slotSize, slotSize);
    slot.border.stroke();

    // Icon position
    slot.icon.x = slotSize / 2;
    slot.icon.y = slotSize / 2;
    slot.icon.width = slotSize - 10;
    slot.icon.height = slotSize - 10;

    // Cooldown text position
    slot.cooldownText.x = slotSize / 2;
    slot.cooldownText.y = slotSize / 2;

    // Stacks text position
    slot.stacksText.x = slotSize - 4;
    slot.stacksText.y = 4;

    // Key label position
    slot.keyLabel.x = slotSize / 2;
    slot.keyLabel.y = -2;

    // Store slot size for updates
    slot.slotSize = slotSize;
  });

  // Position inventory slots under the ability slots
  // Position so that inventory slots 4 and 5 (index 3 and 4) align under ability slots 1 and 2
  const inv = hud.elements.inventorySlots;
  if (inv) {
    const inventoryStartX = hudX - (3 * (slotSize + slotPadding)); // shift left by 3 slots
    inv.slots.forEach((slotObj, i) => {
      const invX = inventoryStartX + i * (slotSize + slotPadding);
      const invY = hudY + slotSize + 8; // small space below ability slots

      slotObj.container.x = invX;
      slotObj.container.y = invY;

      // Placeholder until InventorySlots.update runs (stack gradient or empty fill)
      slotObj.background.clear();
      slotObj.background.rect(0, 0, slotSize, slotSize);
      slotObj.background.alpha = EMPTY_SLOT_FILL_ALPHA;
      slotObj.background.fill({ color: 0xffffff });
      invalidate(slotObj);

      slotObj.border.clear();
      slotObj.border.setStrokeStyle({ width: 2, color: 0x555555 }); // Gray base border
      slotObj.border.rect(0, 0, slotSize, slotSize);
      slotObj.border.stroke();

      slotObj.border.clear();
      slotObj.border.setStrokeStyle({ width: 2, color: 0x555555 });
      slotObj.border.rect(0, 0, slotSize, slotSize);
      slotObj.border.stroke();

      // Position icon sprite
      slotObj.icon.x = slotSize / 2;
      slotObj.icon.y = slotSize / 2;
      slotObj.icon.width = slotSize - 10;
      slotObj.icon.height = slotSize - 10;

      // Position number text centered under the box (local coordinates)
      slotObj.numText.x = slotSize / 2;
      slotObj.numText.y = slotSize + 6;

      // Ammo readout: bottom of the weapon tile, opposite corners
      const ammoRowY = slotSize - 4;
      slotObj.magText.x = 6;
      slotObj.magText.y = ammoRowY;
      slotObj.reserveText.x = slotSize - 5;
      slotObj.reserveText.y = ammoRowY;
    });
    pixiHudLogger.debug('[PixiHUD] Positioned', inv.slots.length, 'inventory slots at hudY:', hudY + slotSize + 8, 'visible:', inv.container.visible);
  }
}

/**
 * Update ability slots
 * @param {Object} gameState - Game state
 * @param {Object} callbacks - Callback functions
 */
export function update(hud, gameState, callbacks) {
  const as = hud.elements.abilitySlots;
  if (!as) return;

  const now = performance.now();
  const abilityState = gameState.abilityState;
  const { getAbilityIcon, getAbility } = callbacks;

  as.slots.forEach((slot) => {
    const equipped = abilityState?.equipped?.[slot.config.category];
    const slotSize = slot.slotSize;

    if (changed(slot, 'border', { equipped: Boolean(equipped) })) {
      slot.background.clear();
      slot.background.rect(0, 0, slotSize, slotSize);
      slot.background.fill({
        color: 0x000000,
        alpha: equipped ? FILLED_SLOT_FILL_ALPHA : EMPTY_SLOT_FILL_ALPHA
      });

      slot.border.clear();
      slot.border.setStrokeStyle({ width: 2, color: equipped ? slot.config.color : 0x555555 });
      slot.border.rect(0, 0, slotSize, slotSize);
      slot.border.stroke();
    }

    if (equipped) {
      const ability = getAbility ? getAbility(equipped.abilityId) : null;

      // Update icon
      const iconTexture = getAbilityIcon ? getAbilityIcon(equipped.abilityId) : null;
      if (iconTexture) {
        slot.icon.texture = iconTexture;
        slot.icon.visible = true;
      } else {
        slot.icon.visible = false;
      }

      // Check cooldowns
      const abilityCooldownEnd = abilityState.cooldowns?.[equipped.abilityId] || 0;
      const globalCooldowns = abilityState.globalCooldowns || {};
      const globalCooldownEnd = globalCooldowns[slot.config.category] || 0;
      const silenceEnd = abilityState.silencedUntil || 0;
      const cooldownEnd = slotUnavailableUntil(
        Math.max(abilityCooldownEnd, globalCooldownEnd),
        silenceEnd
      );
      const isOnCooldown = now < cooldownEnd;

      if (isOnCooldown) {
        const cooldownRemaining = cooldownEnd - now;
        const totalCooldown = (silenceEnd > abilityCooldownEnd && silenceEnd > globalCooldownEnd)
          ? 4000
          : (abilityCooldownEnd > globalCooldownEnd
            ? (ability?.cooldown || 10000)
            : abilityState.globalCooldownDuration);
        const cooldownProgress = Math.min(cooldownRemaining / totalCooldown, 1);

        // Cooldown overlay
        slot.cooldownOverlay.clear();
        slot.cooldownOverlay.rect(2, 2, slotSize - 4, (slotSize - 4) * cooldownProgress);
        slot.cooldownOverlay.fill({ color: 0x000000, alpha: 0.8 });
        slot.cooldownOverlay.visible = true;

        // Cooldown text
        slot.cooldownText.text = Math.ceil(cooldownRemaining / 1000).toString();
        slot.cooldownText.visible = true;
      } else {
        slot.cooldownOverlay.visible = false;
        slot.cooldownText.visible = false;
      }

      // Active effect bar
      const activeEffect = abilityState.activeEffects?.[equipped.abilityId];
      if (activeEffect) {
        const effectRemaining = activeEffect.endTime - now;
        const effectTotal = activeEffect.endTime - activeEffect.startTime;
        const effectProgress = Math.max(0, effectRemaining / effectTotal);

        slot.effectBar.clear();
        slot.effectBar.rect(2, slotSize - 6, (slotSize - 4) * effectProgress, 4);
        slot.effectBar.fill({ color: 0x00ff00 });
        slot.effectBar.visible = true;
      } else {
        slot.effectBar.visible = false;
      }

      // Stacks
      if (equipped.stacks > 1) {
        slot.stacksText.text = `x${equipped.stacks}`;
        slot.stacksText.visible = true;
      } else {
        slot.stacksText.visible = false;
      }
    } else {
      slot.icon.visible = false;
      slot.cooldownOverlay.visible = false;
      slot.cooldownText.visible = false;
      slot.effectBar.visible = false;
      slot.stacksText.visible = false;
    }
  });
}
